<template>
  <debug-widget :data="session" name="session" :open="!session" />
  <div class="session-detail">
    <div v-if="loading" class="loading">Loading session details...</div>

    <div v-else-if="error" class="error">
      {{ error }}
    </div>

    <div v-else-if="session" class="session-content">
      <!-- Session Header -->
      <div class="session-header">
        <h1>Session Details</h1>
        <div class="session-status">
          <status-badge :status="session.status" />
        </div>
      </div>

      <!-- Session Overview -->
      <div class="session-overview">
        <div class="overview-card">
          <h2>Session Overview</h2>
          <div class="overview-grid">
            <div class="overview-item">
              <label>Session ID:</label>
              <span>{{ session.id }}</span>
            </div>
            <div class="overview-item">
              <label>Owner:</label>
              <mini-user
                :online="undefined"
                :fullname="session.user?.fullname"
                :photo_url="session.user?.photo_url"
                :user_id="session.user?.id"
              />
            </div>
            <div class="overview-item">
              <label>Countdown Duration:</label>
              <span>{{ formatDuration(session.countdown_seconds) }}</span>
            </div>
            <div class="overview-item">
              <label>Started By:</label>
              <mini-user
                v-if="session.starter"
                :online="undefined"
                :fullname="session.starter?.fullname"
                :photo_url="session.starter?.photo_url"
                :user_id="session.starter?.id"
                fallback="N/A"
              />
              <div v-else>System</div>
            </div>
            <div class="overview-item">
              <label>Started At:</label>
              <span>{{ formatDateTime(session.started_at) }}</span>
            </div>
            <div class="overview-item">
              <label>Finished At:</label>
              <span>{{ formatDateTime(session.finished_at) }}</span>
            </div>
            <div class="overview-item">
              <label>Countdown Remaining:</label>
              <span>{{
                typeof session.remaining_seconds === 'number'
                  ? formatDuration(session.remaining_seconds)
                  : 'N/A'
              }}</span>
            </div>
            <div class="overview-item">
              <label>Closed At:</label>
              <span>{{ formatDateTime(session.closed_at) }}</span>
            </div>
            <div v-if="session.fail_reason" class="overview-item">
              <label>Fail Reason:</label>
              <span class="fail-reason">{{ formatFailReason(session.fail_reason) }}</span>
            </div>
            <div v-if="session.punishment_text" class="overview-item punishment">
              <label>Punishment:</label>
              <span>{{ session.punishment_text }}</span>
            </div>
          </div>
        </div>

        <!-- Share Link -->
        <div class="share-card">
          <copy-link :url="shareUrl">
            <template #headline>Share Link</template>
            Anyone with this link can view this session's details.
          </copy-link>
        </div>
      </div>

      <!-- Event Log -->
      <div class="event-log">
        <h2>Event Log</h2>
        <div v-if="session.events.length === 0" class="no-events">
          No events recorded for this session.
        </div>
        <div v-else class="events-list">
          <div
            v-for="event in session.events"
            :key="event.id"
            class="event-item"
            :class="event.event_type"
          >
            <div class="event-icon">
              <span :class="event.event_type">{{ getEventIcon(event.event_type) }}</span>
            </div>
            <div class="event-content">
              <div class="event-header">
                <span class="event-type">{{ formatEventType(event.event_type) }}</span>
                <span class="event-time">{{ formatDateTime(event.timestamp) }}</span>
              </div>
              <div v-if="event.user_id" class="event-user">
                Triggered by
                <mini-user
                  :online="undefined"
                  :fullname="event.user?.fullname"
                  :photo_url="event.user?.photo_url"
                  :user_id="event.user?.id"
                  fallback="Unknown user"
                />
              </div>
              <div v-if="event.punishment" class="event-punishment">
                Punishment: {{ event.punishment }}
              </div>
              <div v-if="event.fail_reason" class="event-fail-reason">
                Reason: {{ formatFailReason(event.fail_reason) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useApiFetch } from '../api'
import DebugWidget from '@/components/DebugWidget.vue'
import StatusBadge from '@/components/StatusBadge.vue'
import CopyLink from '@/components/CopyLink.vue'
import MiniUser from '@/components/MiniUser.vue'
import type { SessionStatusOrUnknown } from '@/components/StatusBadge.vue'
import type { UUID, UserOut } from '@/schemas'

interface Event {
  id: number
  session_id: number
  user_id: number | null
  user: UserOut
  event_type: string
  timestamp: string
  punishment: string | null
  fail_reason: string | null
}

interface SessionDetail {
  id: number
  uuid: UUID
  user_id: number
  status: SessionStatusOrUnknown
  countdown_seconds: number
  remaining_seconds: number | null
  starter_user_id: number | null
  starter: UserOut | null
  started_at: string | null
  finished_at: string | null
  closed_at: string | null
  punishment_text: string | null
  fail_reason: string | null
  user: UserOut
  events: Event[]
}

const route = useRoute()
const apiFetch = useApiFetch<SessionDetail>('session detail')

const session = ref<SessionDetail | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)

const shareUrl = computed(() => {
  if (!session.value) return ''
  return `${window.location.origin}/session/${session.value.uuid}`
})

onMounted(async () => {
  const sessionUuid = route.params.uuid as string
  await loadSession(sessionUuid)
})

async function loadSession(uuid: string) {
  loading.value = true
  error.value = null

  try {
    const data = await apiFetch(`/api/sessions/${uuid}/details`)
    session.value = data
  } catch (err) {
    error.value = 'Failed to load session details'
    console.error('Error loading session:', err)
  } finally {
    loading.value = false
  }
}

function formatEventType(eventType: string): string {
  const eventMap: Record<string, string> = {
    ready: 'Session Ready',
    open: 'Session Opened',
    start: 'Started',
    pause: 'Paused',
    restart: 'Restarted',
    reset: 'Reset',
    tick: 'Tick',
    success: 'Success',
    fail: 'Failed',
    abort: 'Aborted',
    close: 'Closed',
    punishment: 'Punishment Added',
    set_length: 'Length Set',
    queue: 'Queued failsafe',
    engage: 'Started via failsafe',
  }
  return eventMap[eventType] || eventType
}

function formatFailReason(reason: string): string {
  const reasonMap: Record<string, string> = {
    timeout: 'Time ran out',
    invalid: 'Invalid attempt',
  }
  return reasonMap[reason] || reason
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return 'N/A'
  return new Date(dateStr).toLocaleString()
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

function getEventIcon(eventType: string): string {
  const iconMap: Record<string, string> = {
    ready: '🚀',
    open: '🔓',
    start: '▶️',
    pause: '⏸️',
    restart: '🔄',
    reset: '↩️',
    tick: '⏱️',
    success: '✅',
    fail: '❌',
    abort: '⏹️',
    close: '🔒',
    punishment: '⚡',
    set_length: '⏰',
    queue: '🔜',
    engage: '⏭️',
  }
  return iconMap[eventType] || '📝'
}
</script>

<style scoped lang="scss">
.session-detail {
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
}

.loading,
.error {
  text-align: center;
  padding: 40px;
  color: var(--text-secondary);
}

.error {
  color: var(--error-color);
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);

  h1 {
    margin: 0;
    color: var(--text-primary);
  }
}

.session-overview {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.overview-card,
.share-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 24px;

  h2,
  h3 {
    margin: 0 0 16px 0;
    color: var(--text-primary);
  }
}

.overview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.overview-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 12px;
    color: var(--text-secondary);
    text-transform: uppercase;
    font-weight: 600;
  }

  span {
    color: var(--text-primary);
    font-weight: 500;
  }

  &.punishment {
    grid-column: 1 / -1;

    span {
      background: var(--status-punishment-bg);
      color: var(--status-punishment);
      padding: 8px 12px;
      border-radius: 4px;
      margin-top: 4px;
    }
  }
}

.fail-reason {
  color: var(--error-color) !important;
}

.event-log {
  h2 {
    margin-bottom: 16px;
    color: var(--text-primary);
  }
}

.no-events {
  text-align: center;
  padding: 40px;
  color: var(--text-secondary);
}

.events-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.event-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
  }

  &.success {
    border-left: 4px solid var(--status-success);
  }

  &.fail {
    border-left: 4px solid var(--status-fail);
  }

  &.start {
    border-left: 4px solid var(--status-started);
  }

  &.ready,
  &.open {
    border-left: 4px solid var(--status-created);
  }

  &.reset,
  &.restart {
    border-left: 4px solid var(--status-reset);
  }

  &.pause {
    border-left: 4px solid var(--status-paused);
  }

  &.abort {
    border-left: 4px solid var(--status-aborted);
  }

  &.punishment {
    border-left: 4px solid var(--status-punishment);
    background: var(--status-punishment-bg);
  }

  &.set_length {
    border-left: 4px solid var(--status-set_length);
  }

  &.queue {
    border-left: 4px solid var(--status-queue);
  }

  &.engage {
    border-left: 4px solid var(--status-engage);
  }
}

.event-icon {
  font-size: 20px;
  line-height: 1;
}

.event-content {
  flex: 1;
}

.event-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.event-type {
  font-weight: 600;
  color: var(--text-primary);
}

.event-time {
  font-size: 12px;
  color: var(--text-secondary);
}

.event-user,
.event-punishment,
.event-fail-reason {
  font-size: 14px;
  color: var(--text-secondary);
  margin-top: 4px;
}

.event-punishment {
  color: var(--error-color);
  font-weight: 500;
}

.event-fail-reason {
  color: var(--error-color);
}
</style>
